import "./CustomerInsights.scss";
import React, { useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import {
  KnowYourCustomerSegmentRow,
  KnowYourCustomerData,
  KnowYourCustomerDataResponse,
} from "@blisspointmedia/bpm-types/dist/KnowYourCustomer";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  FullPageSpinner,
  IconToggleButton,
  InfoTooltip,
  Page,
  DropdownOption,
  ToggleSwitch,
} from "../Components";
import { useCompanyInfo } from "../redux/company";
import { awaitJSON, MiscLambdaFetch } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import CustomerInsightsGroup from "./CustomerInsightsGroup";
import { format } from "date-fns";
import CustomerInsightsGeoGroup from "./CustomerInsightsGeoGroup";
import { useTabbedNav } from "../utils/hooks/useNav";
import { RouteComponentProps } from "@reach/router";
import CustomerInsightsHeader from "./CustomerInsightsHeader";
import {
  CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES,
  CUSTOMER_INSIGHTS_ORDERED_GROUPS,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields,
  NAVS,
  SORT_DROPDOWN_OPTIONS,
  SortOptions,
  TabKey,
  ViewOptions,
} from "./customerInsightsConstants";
import { MdOutlineFileDownload, MdOutlineTableRows, MdShowChart } from "react-icons/md";
import { downloadJSONToCSV } from "../utils/download-utils";

const CustomerInsights = React.memo(({ navigate }: RouteComponentProps) => {
  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "customer-insights",
    defaultKey: TabKey.CUSTOMER_BASE,
    navs: NAVS.map(nav => nav.key, NAVS),
  });
  const { cid, name } = useCompanyInfo();
  const [companyName, setCompanyName] = useState<string>("Tinuiti");
  const setError = useSetError();
  const [data, setData] = useState<Record<string, KnowYourCustomerData[]>>();
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [matchRate, setMatchRate] = useState<{
    matchedPopulation: number;
    totalPopulation: number;
    fraction: number;
  }>();
  const [fetching, setFetching] = useState<boolean>(true);
  const [segmentValue, setSegmentValue] = useState<string>();
  const [allSegments, setAllSegments] = useState<KnowYourCustomerSegmentRow[]>();
  const [sortValue, setSortValue] = useState<SortOptions>("alphabetical");
  const [graphView, setGraphView] = useState<ViewOptions>("showGraph");
  const [suppressUnknown, setSuppressUnknown] = useState<boolean>(true);

  useEffect(() => {
    if (tab === "") {
      goToTab(TabKey.CUSTOMER_BASE);
    }
  }, [tab, goToTab]);

  useEffect(() => {
    if (cid !== "tinuititest") {
      setCompanyName(name);
    }
  }, [cid, name]);

  useEffect(() => {
    if (!data) {
      const fetchData = async () => {
        try {
          setFetching(true);
          const segmentList = await MiscLambdaFetch<{ company: string }>(
            "/list_know_your_customer_configs",
            {
              method: "GET",
              params: {
                company: cid,
              },
            }
          );
          const segmentListJson: KnowYourCustomerSegmentRow[] = await awaitJSON(segmentList);

          const orderedSegments = segmentListJson.sort(row => row.ordering);
          const defaultSegment = orderedSegments.length > 0 ? orderedSegments[0] : undefined;

          if (!defaultSegment) {
            throw new Error("No default Know Your Customer segment found");
          }

          setSegmentValue(defaultSegment.segment_name);
          setAllSegments(segmentListJson);
        } catch (e: any) {
          setError(e.message);
          setFetching(false);
        }
      };

      fetchData();
    }
  }, [cid, data, setError]);

  useEffect(() => {
    const fetchData = async () => {
      if (!allSegments) {
        throw new Error("No segments found");
      }

      const currentSegment = allSegments.find(segment => segment.segment_name === segmentValue);
      if (!currentSegment) {
        throw new Error("Invalid current segment");
      }

      setFetching(true);
      const segmentData = await MiscLambdaFetch<{ id: string }>("/get_know_your_customer_config", {
        method: "GET",
        params: {
          id: String(currentSegment.id),
        },
      });
      const { rows, lastUpdated }: KnowYourCustomerDataResponse = await awaitJSON(segmentData);
      setLastUpdated(new Date(lastUpdated));

      const typedRows: KnowYourCustomerData[] = rows.map(row => {
        return {
          clientPopulation: Number(row.client_population),
          clientPopulationFraction: Number(row.client_population_fraction),
          fieldName: row.field_name,
          levels: row.levels,
          tuPopulation: Number(row.tu_population),
          tuPopulationFraction: Number(row.tu_population_fraction),
        };
      });
      const groupedRows = R.groupBy(row => row.fieldName, typedRows);

      const matchRateRows = groupedRows[CustomerInsightsFields.MATCH_RATE];
      if (matchRateRows && matchRateRows.length > 0) {
        const matchRate = matchRateRows[0];
        setMatchRate({
          matchedPopulation: matchRate?.clientPopulation,
          totalPopulation: Math.round(
            matchRate?.clientPopulation / matchRate?.clientPopulationFraction
          ),
          fraction: matchRate?.clientPopulationFraction,
        });
      }

      delete groupedRows[""];
      setData(groupedRows);
      setFetching(false);
    };

    if (allSegments) {
      fetchData();
    }
  }, [allSegments, segmentValue, setError]);

  const unknownSuppressedData: Record<string, KnowYourCustomerData[]> = useMemo(() => {
    if (!data) {
      return {};
    }

    const modifiedData = R.clone(data);

    Object.keys(modifiedData).forEach(key => {
      const groups = modifiedData[key];
      const unknown = groups.find(group => group.levels === "Unknown");

      if (unknown) {
        const indexOfUnknown = groups.indexOf(unknown);
        groups.splice(indexOfUnknown, 1);

        // Recalculate fractions without Unknown
        const clientPopSum = groups.reduce((acc, group) => {
          return acc + group.clientPopulation;
        }, 0);
        const tuPopSum = groups.reduce((acc, group) => {
          return acc + group.tuPopulation;
        }, 0);
        groups.forEach(group => {
          group.clientPopulationFraction = group.clientPopulation / clientPopSum;
          group.tuPopulationFraction = group.tuPopulation / tuPopSum;
        });
      }
    });

    return modifiedData;
  }, [data]);

  const body = useMemo(() => {
    if (!data && fetching) {
      return <FullPageSpinner />;
    }

    if (!data && !fetching) {
      return <div>TODO: Empty State</div>;
    }

    if (data) {
      return (
        <div className="customerInsightsBody">
          <CustomerInsightsHeader tab={tab} matchRate={matchRate} />
          <CustomerInsightsGroup
            fields={CUSTOMER_INSIGHTS_ORDERED_GROUPS.household}
            data={suppressUnknown ? unknownSuppressedData : data}
            header={
              <>
                What types of households do {companyName} customers reside in?
                <InfoTooltip>
                  The total number of households represented below may not equal the number of
                  emails ingested due to differing levels of identity resolution (individual,
                  household, etc.) across attributes.
                </InfoTooltip>
              </>
            }
            sortValue={sortValue}
            graphView={graphView}
            tab={tab}
            suppressUnknown={suppressUnknown}
          />
          <CustomerInsightsGroup
            fields={CUSTOMER_INSIGHTS_ORDERED_GROUPS.individual}
            data={suppressUnknown ? unknownSuppressedData : data}
            header={`What types of individuals form ${companyName}'s customer base?`}
            sortValue={sortValue}
            graphView={graphView}
            tab={tab}
            suppressUnknown={suppressUnknown}
          />
          <CustomerInsightsGeoGroup
            data={
              suppressUnknown
                ? unknownSuppressedData[CustomerInsightsFields.MSA]
                : data[CustomerInsightsFields.MSA]
            }
            totalPopulation={matchRate?.totalPopulation ?? 0}
            header={`Where do ${companyName} customers reside?`}
            tab={tab}
            graphView={graphView}
          />
        </div>
      );
    }
  }, [
    data,
    fetching,
    tab,
    matchRate,
    suppressUnknown,
    unknownSuppressedData,
    companyName,
    sortValue,
    graphView,
  ]);

  return (
    <Page
      app2Redesign
      title="Customer Insights"
      pageType="Customer Insights"
      navs={NAVS}
      selectedNav={tab}
      onNav={goToTab}
      actions={
        <div className="customerInsightsActions">
          {lastUpdated && (
            <div className="lastUpdated">Last Updated: {format(lastUpdated, "M/d/yy h:mmaaa")}</div>
          )}
          {segmentValue && allSegments && allSegments.length > 1 && (
            <div className="kpiContainer">
              <Dropdown
                type={DropdownToggleType.FILLED}
                value={segmentValue}
                label="Segment"
                options={
                  allSegments
                    .sort((a, b) => a.ordering - b.ordering)
                    .map(row => ({
                      value: row.segment_name,
                      label: row.segment_name,
                    })) as DropdownOption[]
                }
                onChange={change => setSegmentValue(change)}
                background="dark"
              />
            </div>
          )}
          <ToggleSwitch
            background="dark"
            design="primary"
            size="lg"
            buttonClassName="unknownToggle"
            label="Hide Unknown"
            checked={suppressUnknown}
            onChange={() => setSuppressUnknown(!suppressUnknown)}
            infoTooltipText="When an email address is recognized in the identity graph, specific demographic characteristics (e.g., ethnicity) may not be known with high confidence. In these cases, a value of &acute;Unknown&acute; will be returned for that characteristic for that user. You can suppress these values by using the Hide &acute;Unknown&acute; button to make relative comparisons easier to visualize."
          />
          <div className="sortContainer">
            <Dropdown
              type={DropdownToggleType.FILLED}
              value={sortValue}
              label="Sort by"
              options={SORT_DROPDOWN_OPTIONS}
              onChange={change => setSortValue(change)}
              background="dark"
            />
          </div>
          <IconToggleButton
            options={[
              { key: "showTable", icon: <MdOutlineTableRows />, label: "table view" },
              { key: "showGraph", icon: <MdShowChart />, label: "graph view" },
            ]}
            size="sm"
            background="dark"
            selectedOption={graphView}
            onChange={() =>
              setGraphView(prev => (prev === "showGraph" ? "showTable" : "showGraph"))
            }
          />
          <Button
            type={ButtonType.FILLED}
            variant={ButtonFrameworkVariant.ICON_ONLY}
            background="dark"
            icon={<MdOutlineFileDownload />}
            onClick={() => {
              if (!data) {
                setError({ message: "No data available to download" });
                return;
              }

              let splicedDataKeys = Object.keys(data);
              const matchRateIndex = splicedDataKeys.findIndex(
                item => item === CustomerInsightsFields.MATCH_RATE
              );
              if (matchRateIndex >= 0) {
                splicedDataKeys.splice(matchRateIndex, 1);
              }

              const formattedData = splicedDataKeys
                .map(fieldName => {
                  const rows = data[fieldName];
                  const fieldPrettyName: string = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];

                  return rows.map(row => {
                    const levelsPrettyName: string =
                      CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES[fieldName][row.levels] ?? row.levels;

                    return {
                      "Demographic Category": fieldPrettyName,
                      "Demographic Group": levelsPrettyName,
                      [`${companyName}'s Population`]: Math.round(row.clientPopulation),
                      [`${companyName}'s Distribution`]: row.clientPopulationFraction,
                      "US Market": Math.round(row.tuPopulation),
                      "US Market Distribution": row.tuPopulationFraction,
                      Index: row.clientPopulationFraction / row.tuPopulationFraction,
                      Penetration: row.clientPopulation / row.tuPopulation,
                    };
                  });
                })
                .flat();

              const date = new Date();
              downloadJSONToCSV(
                formattedData,
                `CustomerInsights ${companyName} ${date.toLocaleString()}`
              );
            }}
          />
        </div>
      }
    >
      {body}
    </Page>
  );
});

export default CustomerInsights;
